import classNames from "classnames";
import { useEffect, useId, useRef } from "react";
import type {
  FieldErrors,
  FieldPath,
  FieldValues,
  PathValue,
  RegisterOptions,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  type DateInputOnChangeValue,
  DateRangeInput as MantineDateRangeInput
} from "../../../../../../mantine/dates/DateRangeInput/DateRangeInput";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTimeOrNull } from "../../../../../../utils/dates/luxonDateTimeToBackendDateOrDateTimeOrNull";
import { FormFieldInfoText } from "../../../MantineFormFields/FormFieldInfoText/FormFieldInfoText";
import { FormFieldLabel } from "../../../MantineFormFields/FormFieldLabel";
import { HelperText } from "../../../MantineFormFields/HelperText/HelperText";
import { getErrorKeyFromFieldError } from "../../../MantineFormFields/HelperText/utils/getErrorKeyFromFieldError";
import { isFieldError } from "../../../MantineFormFields/HelperText/utils/isFieldError";
import "./FormDateRangeInput.scss";

interface FormDateRangeInputProps<
  T extends FieldValues,
  TName extends FieldPath<T>
> {
  registerOptions?: {
    [key in TName]: RegisterOptions<T, TName>;
  };
  startDateFieldName: TName;
  endDateFieldName: TName;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  unregister: UseFormUnregister<T>;
  watch: UseFormWatch<T>;
  errors: FieldErrors<T>;
  label?: string;
  infoText?: string;
}

function FormDateRangeInput<T extends FieldValues, TName extends FieldPath<T>>({
  registerOptions,
  startDateFieldName,
  endDateFieldName,
  register,
  setValue,
  watch,
  errors,
  label = "Zeitraum",
  infoText
}: FormDateRangeInputProps<T, TName>) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const startInputId = useId();

  useEffect(() => {
    register(startDateFieldName, {
      required: registerOptions?.[startDateFieldName].required
    });
    register(endDateFieldName, {
      required: registerOptions?.[endDateFieldName].required
    });
  }, [startDateFieldName, endDateFieldName, registerOptions, register]);

  const startDateError = isFieldError(errors[startDateFieldName])
    ? errors[startDateFieldName]
    : undefined;
  const endDateError = isFieldError(errors[endDateFieldName])
    ? errors[endDateFieldName]
    : undefined;
  const dateRangeErrorKey = getErrorKeyFromFieldError(
    startDateError || endDateError
  );
  const dateRangeError = dateRangeErrorKey && t(dateRangeErrorKey);

  const required =
    registerOptions?.[startDateFieldName]?.required ||
    registerOptions?.[endDateFieldName]?.required;

  const startDateValue = watch(startDateFieldName);
  const endDateValue = watch(endDateFieldName);

  function handleDatePickerChange(newRange: DateInputOnChangeValue) {
    const shouldValidate = !!startDateError || !!endDateError;

    setValue(
      startDateFieldName,
      luxonDateTimeToBackendDateOrDateTimeOrNull(newRange[0]) as PathValue<
        T,
        TName
      >,
      { shouldValidate }
    );
    setValue(
      endDateFieldName,
      luxonDateTimeToBackendDateOrDateTimeOrNull(newRange[1]) as PathValue<
        T,
        TName
      >,
      { shouldValidate }
    );
  }

  return (
    <div
      className={classNames("FormDateRangeInput", {
        "is-invalid": dateRangeError
      })}
      ref={ref}
    >
      <FormFieldLabel
        htmlFor={startInputId}
        label={label}
        required={required === true}
      />
      <MantineDateRangeInput
        startInputProps={{ id: startInputId }}
        value={[
          backendDateOrDateTimeToLuxonDateTimeOrNull(startDateValue),
          backendDateOrDateTimeToLuxonDateTimeOrNull(endDateValue)
        ]}
        onChange={handleDatePickerChange}
      />
      <HelperText error={dateRangeError} />
      {infoText && <FormFieldInfoText infoText={infoText} />}
    </div>
  );
}

export { FormDateRangeInput, FormDateRangeInputProps };
